import { useTranslation } from 'react-i18next'
import { useGetSiteIdentity } from '@/application/services/settings.service'
import { useJsonLd } from '@/application/hooks/useJsonLd'
import { resolveLanguage } from '@/application/i18n/language'
import { PRIMARY_NAV_SECTIONS } from '@/application/seo/navigation'
import {
  absoluteUrl,
  localizedAbsoluteUrl,
  OG_IMAGE_URL,
  PUBLISHER_LD_ID,
  PUBLISHER_NAME,
  SITE_URL,
  WEBSITE_LD_ID,
} from '@/application/seo/site'

/**
 * Site-wide JSON-LD (`WebSite` + `Person`), injected once from `PublicLayout`
 * rather than duplicated per page. `Person` describes Bertrand Presles as the
 * site's individual publisher, mirroring the identity already stated on the
 * legal pages (mentions légales / privacy policy).
 *
 * Both nodes carry a stable `@id` on the CANONICAL origin, and the `WebSite`
 * points its `publisher` at the `Person`'s `@id` rather than repeating the
 * identity inline. That is what lets Google merge the two nodes — and the
 * per-article node emitted by `InvestigationReadView` — into one graph instead
 * of reading them as three unrelated entities. An `@id` built from the request
 * host would mint a second, competing identity for apex visitors, so it is
 * built from `SITE_URL` like every other URL this app emits.
 *
 * Both nodes keep the UNPREFIXED root as their `url`, whatever language is on
 * screen. They describe the site and its publisher, not the page: there is one
 * Balivernologie and one Bertrand Presles, and re-pointing their `url` at
 * `/en/` per visit would contradict their fixed `@id` and read as three
 * entities again. Per-page, per-language URLs are the canonical/hreflang set's
 * job (`usePageLangAndHreflang`); `inLanguage` below is what tells Google which
 * language variant produced this particular rendering.
 */
export function PublicJsonLd() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)
  const { data: siteIdentity } = useGetSiteIdentity(lang)

  const siteName = siteIdentity?.siteName || t('app_name')
  const siteDescription = siteIdentity?.siteDescription

  useJsonLd('ld-json-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_LD_ID,
    name: siteName,
    ...(siteDescription ? { description: siteDescription } : {}),
    url: absoluteUrl('/'),
    inLanguage: lang,
    publisher: { '@id': PUBLISHER_LD_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/investigations?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  })

  useJsonLd('ld-json-person', {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': PUBLISHER_LD_ID,
    name: PUBLISHER_NAME,
    url: absoluteUrl('/'),
    image: OG_IMAGE_URL,
  })

  useJsonLd('ld-json-navigation', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: PRIMARY_NAV_SECTIONS.map((section, index) => ({
      '@type': 'SiteNavigationElement',
      position: index + 1,
      name: t(section.labelKey),
      url: localizedAbsoluteUrl(section.path, lang),
    })),
  })

  return null
}
